import { SubscriptionDto } from '@/Domain/api/Subscription/dto/Subscription.dto';
import { SubscriptionIntervalEnum } from '@/Domain/enum/SubscriptionInterval.enum';
import { TierEnum } from '@/Domain/enum/Tier.enum';

export type PricingPlanKey =
  | 'free'
  | 'starter'
  | 'basic'
  | 'essential'
  | 'professional'
  | 'business'
  | 'custom';

export type PricingPlan = {
  key: PricingPlanKey;
  tier: TierEnum;
  monthlyPrice: number;
  monthlyCredits?: number;
  isCustom?: boolean;
};

// Display prices only; the Stripe price ID that actually charges comes from the
// offer (stripeData.plans) inside ChoosePlanButton. Annual total = monthly * 10.
// Credits mirror free_messages in payments/feature_bundles/pricing_plans.yaml.
export const PRICING_PLANS: PricingPlan[] = [
  { key: 'free', tier: TierEnum.FREE, monthlyPrice: 0, monthlyCredits: 50 },
  { key: 'starter', tier: TierEnum.STARTER, monthlyPrice: 9, monthlyCredits: 150 },
  { key: 'basic', tier: TierEnum.BASIC_2026, monthlyPrice: 29, monthlyCredits: 500 },
  { key: 'essential', tier: TierEnum.ESSENTIAL_2026, monthlyPrice: 99, monthlyCredits: 3000 },
  {
    key: 'professional',
    tier: TierEnum.PROFESSIONAL_2026,
    monthlyPrice: 299,
    monthlyCredits: 9000,
  },
  { key: 'business', tier: TierEnum.BUSINESS_2026, monthlyPrice: 999, monthlyCredits: 30000 },
  { key: 'custom', tier: TierEnum.CUSTOM, monthlyPrice: 0, isCustom: true },
];

// Ascending chain: each paid plan lists only what it adds over the previous one.
export const PRICING_CHAIN: PricingPlanKey[] = [
  'free',
  'starter',
  'basic',
  'essential',
  'professional',
  'business',
];

export const ENTERPRISE_FROM_PRICE = '0.50';

export type PlanRelation = 'current' | 'upcoming' | 'downgrade' | 'next' | 'higher' | 'update';

const LINEUP_ORDER: TierEnum[] = PRICING_PLANS.map((plan) => plan.tier);

// Legacy paid tiers map to the same-named new-pricing plan, so they sit at that
// plan's position in the lineup. The equivalent plan offers an "Update" instead
// of "Current plan"; everything else compares by index just like a new plan.
const LEGACY_TIER_EQUIVALENT: Partial<Record<TierEnum, TierEnum>> = {
  [TierEnum.BASIC]: TierEnum.BASIC_2026,
  [TierEnum.ESSENTIAL]: TierEnum.ESSENTIAL_2026,
  [TierEnum.PROFESSIONAL]: TierEnum.PROFESSIONAL_2026,
  [TierEnum.BUSINESS]: TierEnum.BUSINESS_2026,
};

// Position of the customer's plan in the lineup; -1 for tiers outside it (e.g. trial).
export const getCurrentLineupIndex = (currentTier: TierEnum): number =>
  LINEUP_ORDER.indexOf(LEGACY_TIER_EQUIVALENT[currentTier] ?? currentTier);

// Where a plan sits relative to the customer's current plan, driving the CTA's
// label, variant and disabled state. Legacy current tiers (not in the lineup)
// treat every new plan as a choosable upgrade.
export const getPlanRelation = (
  plan: PricingPlan,
  currentPlan: SubscriptionDto['current_plan'],
  subscription: SubscriptionDto['subscription'],
  isBillingYearly: boolean,
): PlanRelation => {
  const interval = isBillingYearly
    ? SubscriptionIntervalEnum.Annually
    : SubscriptionIntervalEnum.Monthly;

  const isActive =
    currentPlan.tier === plan.tier &&
    (currentPlan.interval === interval ||
      currentPlan.interval === SubscriptionIntervalEnum.OneTime);
  if (isActive) {
    return 'current';
  }
  if (subscription.next_tier === plan.tier && subscription.next_tier_interval === interval) {
    return 'upcoming';
  }
  const legacyEquivalent = LEGACY_TIER_EQUIVALENT[currentPlan.tier];
  const currentIndex = getCurrentLineupIndex(currentPlan.tier);
  if (currentIndex < 0) {
    return 'higher';
  }
  const planIndex = LINEUP_ORDER.indexOf(plan.tier);
  if (legacyEquivalent && planIndex === currentIndex) {
    return 'update';
  }
  if (planIndex < currentIndex) {
    return 'downgrade';
  }
  if (planIndex === currentIndex + 1) {
    return 'next';
  }
  return 'higher';
};
